import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Row, Input, Table, DatePicker } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  getInwardItemsForQualityCheckTable,
  clearInwardItemsForQualityCheckTable,
  getColorIds,
  getFactoryIds,
  getRackIds,
  clearColorIds,
  clearFactoryIds,
  clearRackIds,
  getStoreInward,
  clearStoreInward,
  clearQualityCheckFiles,
} from '../Actions';
import ItemForm from './ItemForm';
import FabricForm from './FabricForm';
import AccessoryForm from './AccessoryForm';
import StyleForm from './StyleForm';

const AntInput = GetField(Input);
const AntDatePicker = GetField(DatePicker);
const AntInputTextArea = GetField(Input.TextArea);

const CreateQualityCheck = props => {
  const { classes, match } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { handleSubmit, control, getValues, watch, setValue, unregister } =
    useForm({
      shouldUnregister: false,
      defaultValues: {
        fabrics: [{ productId: null, fabricId: null }],
        accessories: [{ productId: null, accessoryId: null }],
        items: [{ name: null, shade: null, size: null }],
        styles: [
          {
            productId: null,
            styleId: null,
            baseColorId: null,
            printColorId: null,
            embroideryColorId: null,
            acidWashColorId: null,
          },
        ],
      },
    });

  const {
    fields: fabricFields,
    append: fabricAppend,
    remove: fabricRemove,
  } = useFieldArray({
    control,
    name: 'fabrics',
  });

  const {
    fields: accessoryFields,
    append: accessoryAppend,
    remove: accessoryRemove,
  } = useFieldArray({
    control,
    name: 'accessories',
  });

  const {
    fields: itemFields,
    append: itemAppend,
    remove: itemRemove,
  } = useFieldArray({
    control,
    name: 'items',
  });

  const {
    fields: styleFields,
    append: styleAppend,
    remove: styleRemove,
  } = useFieldArray({
    control,
    name: 'styles',
  });

  const [confirmLoading, setConfirmLoading] = useState(false);

  const storeInward = useSelector(state => state.storeInward);

  const InputTable = useSelector(
    state => state.inwardItemsForQualityCheckInputTable
  );

  const qcFiles = useSelector(state => state.qualityCheckFiles);

  useEffect(() => {
    dispatch(getStoreInward(match.params.id));
    dispatch(getInwardItemsForQualityCheckTable(match.params.id));
    dispatch(getColorIds());
    dispatch(getFactoryIds());
    dispatch(getRackIds());

    return () => {
      dispatch(clearStoreInward());
      dispatch(clearInwardItemsForQualityCheckTable());
      dispatch(clearColorIds());
      dispatch(clearFactoryIds());
      dispatch(clearRackIds());
      dispatch(clearQualityCheckFiles());
    };
  }, []);

  useEffect(async () => {
    try {
      const { data } = await Api.get('/quality-checks/newqcno');
      setValue('newqcNo', data);
    } catch (error) {
      errorNotification(error.message);
    }
  }, []);

  useEffect(() => {
    setValue('customer', InputTable.customer);
    setValue('bpoNo', InputTable.bpoNo);
    setValue('vendor', InputTable.vendor);
    setValue('rpoNo', InputTable.rpoNo);
    setValue('rpoDate', InputTable.rpoDate);
    setValue('inwardDate', InputTable.inwardDate);
    setValue('inwardNo', InputTable.inwardNo);
    setValue('isJobOrder', InputTable.isJobOrder);
    setValue('process', InputTable.process);
  }, [InputTable]);

  const fabricRowKey = useMemo(
    () => record => `P${record.productId}F${record.fabricId}C${record.colorId}`,
    []
  );

  const accessoryRowKey = useMemo(
    () => record => `P${record.productId}A${record.accessoryId}`,
    []
  );

  const itemRowKey = useMemo(
    () => record =>
      `N${record.name}S${record.shade}S${record.size}U${record.unitId}`,
    []
  );

  const styleRowKey = useMemo(
    () => record =>
      `P${record.productId}S${record.styleId}B${record.baseColor}P${record.printColor}`,
    []
  );

  const scroll = useMemo(() => ({ x: 2200 }), []);

  const fabricsColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
      {
        title: 'Inward Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Invoice Date',
        dataIndex: 'invoiceDate',
        render: invoiceDate =>
          invoiceDate ? moment(invoiceDate).format('DD-MM-YYYY') : null,
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'Challan Date',
        dataIndex: 'challanDate',
        render: challanDate =>
          challanDate ? moment(challanDate).format('DD-MM-YYYY') : null,
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const accessoryColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
      {
        title: 'Inward Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Invoice Date',
        dataIndex: 'invoiceDate',
        render: invoiceDate =>
          invoiceDate ? moment(invoiceDate).format('DD-MM-YYYY') : null,
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'Challan Date',
        dataIndex: 'challanDate',
        render: challanDate =>
          challanDate ? moment(challanDate).format('DD-MM-YYYY') : null,
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const itemsColumns = useMemo(
    () => [
      {
        title: 'Item',
        dataIndex: 'name',
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
      {
        title: 'Inward Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Invoice Date',
        dataIndex: 'invoiceDate',
        render: invoiceDate =>
          invoiceDate ? moment(invoiceDate).format('DD-MM-YYYY') : null,
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'Challan Date',
        dataIndex: 'challanDate',
        render: challanDate =>
          challanDate ? moment(challanDate).format('DD-MM-YYYY') : null,
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const stylesColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'style',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
      {
        title: 'Inward Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Invoice Date',
        dataIndex: 'invoiceDate',
        render: invoiceDate =>
          invoiceDate ? moment(invoiceDate).format('DD-MM-YYYY') : null,
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'Challan Date',
        dataIndex: 'challanDate',
        render: challanDate =>
          challanDate ? moment(challanDate).format('DD-MM-YYYY') : null,
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);

      const {
        itemsIndexes,
        fabricsIndexes,
        accessoriesIndexes,
        stylesIndexes,
      } = qcFiles;

      const formData = new FormData();

      formData.append(
        'data',
        JSON.stringify({
          ...formValues,
          storeInwardId: match.params.id,
          itemsIndexes,
          fabricsIndexes,
          accessoriesIndexes,
          stylesIndexes,
        })
      );

      let index = 0;

      for (const key of Object.keys(qcFiles.items)) {
        if (qcFiles.items[key] && qcFiles.items[key].files.length) {
          for (const file of qcFiles.items[key].files) {
            formData.append(`files[${index}]`, file);
            index += 1;
          }
        }
      }

      for (const key of Object.keys(qcFiles.fabrics)) {
        if (qcFiles.fabrics[key] && qcFiles.fabrics[key].files.length) {
          for (const file of qcFiles.fabrics[key].files) {
            formData.append(`files[${index}]`, file);
            index += 1;
          }
        }
      }

      for (const key of Object.keys(qcFiles.accessories)) {
        if (qcFiles.accessories[key] && qcFiles.accessories[key].files.length) {
          for (const file of qcFiles.accessories[key].files) {
            formData.append(`files[${index}]`, file);
            index += 1;
          }
        }
      }

      for (const key of Object.keys(qcFiles.styles)) {
        if (qcFiles.styles[key] && qcFiles.styles[key].files.length) {
          for (const file of qcFiles.styles[key].files) {
            formData.append(`files[${index}]`, file);
            index += 1;
          }
        }
      }

      await Api.post('/quality-checks', formData, {
        headers: { 'content-type': 'multipart/form-data' },
      });

      successNotification('Quality check created');
      history.push('/qualitycheck/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Quality Check"
        moduleLink="/qualitycheck/list"
        subModuleName="Create"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Customer"
                  placeholder="Customer"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="bpoNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="BPO #"
                  placeholder="BPO #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="vendor"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Vendor"
                  placeholder="Vendor"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="rpoNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="RPO #"
                  placeholder="RPO #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="rpoDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  label="RPO Date"
                  disabled
                  placeholder="RPO Date"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="newqcNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Quality Check #"
                  placeholder="Quality Check #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="inwardNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Inward #"
                  placeholder="Inward #"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="inwardDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  label="Inward Date"
                  disabled
                  placeholder="Inward Date"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="qcDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  label="Quality Check Date"
                  allowClear
                  placeholder="Quality Check Date"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          {storeInward.isJobOrder ? (
            <RightGrid>
              <Controller
                name="process"
                control={control}
                render={({ field }) => (
                  <AntInput
                    disabled
                    label="Process"
                    placeholder="Process"
                    {...field}
                  />
                )}
              />
            </RightGrid>
          ) : null}
        </Row>
        <div className={classes.previewMainHeading}>
          Store Inward Data Table
        </div>
        <br />
        {storeInward.items?.length ? (
          <>
            <div className={classes.tableStyle}>
              <Table
                bordered
                size="small"
                columns={itemsColumns}
                dataSource={storeInward.items}
                rowKey={itemRowKey}
                pagination={false}
                scroll={scroll}
                title={() => 'Items Table'}
              />
            </div>
            <br />
          </>
        ) : null}
        {storeInward.fabrics?.length ? (
          <>
            <div className={classes.tableStyle}>
              <Table
                bordered
                size="small"
                columns={fabricsColumns}
                dataSource={storeInward.fabrics}
                rowKey={fabricRowKey}
                pagination={false}
                scroll={scroll}
                title={() => 'Fabrics Table'}
              />
            </div>
            <br />
          </>
        ) : null}
        {storeInward.accessories?.length ? (
          <>
            <div className={classes.tableStyle}>
              <Table
                bordered
                size="small"
                columns={accessoryColumns}
                dataSource={storeInward.accessories}
                rowKey={accessoryRowKey}
                pagination={false}
                scroll={scroll}
                title={() => 'Accessories Table'}
              />
            </div>
            <br />
          </>
        ) : null}
        {storeInward.styles?.length ? (
          <>
            <div className={classes.tableStyle}>
              <Table
                bordered
                size="small"
                columns={stylesColumns}
                dataSource={storeInward.styles}
                rowKey={styleRowKey}
                pagination={false}
                scroll={scroll}
                title={() => 'Products Table'}
              />
            </div>
            <br />
          </>
        ) : null}
        <br />
        <div className={classes.previewMainHeading}>
          Quality Check Input Table
        </div>
        <br />
        {InputTable.items.names.length ? (
          <>
            <div className={classes.previewSubHeading}>Items Table</div>
            <Row>
              <div className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr key="header">
                      <th className={classes.inputTh}>Item</th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Shade
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Size
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Unit
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Condition
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Remark
                      </th>
                      <th className={classes.inputTh} style={{ width: '60px' }}>
                        Files
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemFields.map((item, index) => (
                      <ItemForm
                        key={item.id}
                        itemItem={item}
                        itemIndex={index}
                        itemAppend={itemAppend}
                        itemRemove={itemRemove}
                        getValues={getValues}
                        setValue={setValue}
                        unregister={unregister}
                        control={control}
                        watch={watch}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Row>
            <br />
          </>
        ) : null}
        {InputTable.fabrics.productNames.length ? (
          <>
            <div className={classes.previewSubHeading}>Fabrics Table</div>
            <Row>
              <div className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr key="header">
                      {InputTable.isProductColumn ? (
                        <th
                          className={classes.inputTh}
                          style={{ width: '15%' }}
                        >
                          Product
                        </th>
                      ) : null}
                      <th className={classes.inputTh}>Fabric</th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Width
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Base Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Print Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Factory
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Rack #
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Condition
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Remark
                      </th>
                      <th className={classes.inputTh} style={{ width: '60px' }}>
                        Files
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fabricFields.map((item, index) => (
                      <FabricForm
                        key={item.id}
                        fabricItem={item}
                        fabricIndex={index}
                        fabricAppend={fabricAppend}
                        fabricRemove={fabricRemove}
                        getValues={getValues}
                        setValue={setValue}
                        unregister={unregister}
                        control={control}
                        watch={watch}
                        isProductColumn={InputTable.isProductColumn}
                        isJobOrder={InputTable.isJobOrder}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Row>
            <br />
          </>
        ) : null}
        {InputTable.accessories.productNames.length ? (
          <>
            <div className={classes.previewSubHeading}>Accessories Table</div>
            <Row>
              <div className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr key="header">
                      {InputTable.isProductColumn ? (
                        <th
                          className={classes.inputTh}
                          style={{ width: '15%' }}
                        >
                          Product
                        </th>
                      ) : null}
                      <th className={classes.inputTh}>Accessory</th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Factory
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Rack #
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Condition
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Remark
                      </th>
                      <th className={classes.inputTh} style={{ width: '60px' }}>
                        Files
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessoryFields.map((item, index) => (
                      <AccessoryForm
                        key={item.id}
                        accessoryItem={item}
                        accessoryIndex={index}
                        accessoryAppend={accessoryAppend}
                        accessoryRemove={accessoryRemove}
                        getValues={getValues}
                        setValue={setValue}
                        unregister={unregister}
                        control={control}
                        watch={watch}
                        isProductColumn={InputTable.isProductColumn}
                        isJobOrder={InputTable.isJobOrder}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Row>
            <br />
          </>
        ) : null}
        {InputTable.styles.products.length ? (
          <>
            <div className={classes.previewSubHeading}>Products Table</div>
            <Row>
              <div className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr key="header">
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Product
                      </th>
                      <th className={classes.inputTh}>Style</th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Description
                      </th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Size
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Base Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Print Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Embroidery Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Acid Wash Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Condition
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Remark
                      </th>
                      <th className={classes.inputTh} style={{ width: '60px' }}>
                        Files
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {styleFields.map((item, index) => (
                      <StyleForm
                        key={item.id}
                        styleItem={item}
                        styleIndex={index}
                        styleAppend={styleAppend}
                        styleRemove={styleRemove}
                        getValues={getValues}
                        setValue={setValue}
                        unregister={unregister}
                        control={control}
                        watch={watch}
                        isJobOrder={InputTable.isJobOrder}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Row>
            <br />
          </>
        ) : null}
        <Row>
          <LeftGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  label="Remark"
                  allowClear
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </LeftGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

CreateQualityCheck.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(CreateQualityCheck);
